import {Head, Link, router} from '@inertiajs/react'
import Layout from "../../components/layout";
import {ArrowLeftIcon, ArrowUpTrayIcon, LockClosedIcon, SparklesIcon, UserIcon} from "@heroicons/react/24/outline";
import Album from '../../components/album.jsx';
import React, {useCallback, useEffect, useState} from 'react';
import AuthForm from '../../components/authForm.jsx';
import DarkModal from '../../components/darkModal.jsx';
import {Trans, useTranslation} from 'react-i18next';
import {Blurhash} from 'react-blurhash';
import MadeWithBadge from '../../components/madeWithBadge.jsx';

const blurhashes = [
    'LCA,tTRWR8RP0J%MxuogNZR$Riof',
    'LEBypnr@aLV]0K%2xus;JBNGNGbF',
    'L9AAXU%NRh%DDgM_xvbL%hM}t3M|',
    'LcGIcYbDt6IU~qj?WBM{Rjt7WBof',
    'LHFYS:~pNHkBRONH-on%I9R4bEX8',
    'LGAAHwt7IVxuD*j@tPR*0LWBxaNG',
    'LFAmPFxuE1of9GWVxaa|0LNGxuR*',
    'LJAJs8oKE1a{NGofoej@0LWV%Lj[',
    'LWIqMtWBE1%20Moe%2WB57NHRkRk',
    'LzJH28f6Rjj[0Mj[ozayNaaye.fQ',
];

const HiddenAlbum = ({reveal, onViewOwn}) => {
    const {t} = useTranslation();

    return (
        <div className="relative grow mt-2 overflow-hidden">
            <div className="absolute inset-0 grid grid-cols-2 grid-rows-5 gap-1">
                {blurhashes.map((hash, i) => (
                    <div key={i} className="overflow-hidden rounded-lg opacity-40">
                        <Blurhash hash={hash} width="100%" height="100%" />
                    </div>
                ))}
            </div>
            <div className="relative flex flex-col items-center justify-start pt-[25%] px-5 pb-5">
                <div
                    className="flex items-center justify-center w-16 h-16"
                    style={{background: 'radial-gradient(circle, rgba(239,68,68,0.28), transparent 70%)'}}
                >
                    <LockClosedIcon className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-white-foreground font-bold text-2xl mt-3 mb-2 text-center">
                    {t('Come back later!')}
                </h2>
                <p className="text-white-foreground text-center text-sm">
                    <Trans i18nKey="revealComeBack">Photos will be revealed at <span
                        className="font-semibold">{{reveal}}</span>. Please come back then.</Trans>
                </p>
                <button
                    onClick={onViewOwn}
                    className="mt-4 button-backdrop text-white-foreground px-5 py-2 rounded-[10px] font-medium"
                >
                    {t('View your photos')}
                </button>
            </div>
        </div>
    );
};

export default function PhotosIndex({
                                        event,
                                        photos,
                                        backUrl,
                                        aspectRatio,
                                        effects,
                                        currentEffect,
                                        participants = [],
                                        currentParticipant = null,
                                        uploadUrl,
                                        allowBack,
                                        reloadUrl,
                                        has_account,
                                        pageTitleSuffix = 'Camera',
                                        showBranding = false
                                    }) {
    const {t} = useTranslation();
    const [effect, setEffect] = useState(currentEffect);
    const [participant, setParticipant] = useState(currentParticipant ?? '');
    const [open, setOpen] = useState(false);
    const [viewOwn, setViewOwn] = useState(false);

    const isPaginated = photos && !Array.isArray(photos);
    const items = isPaginated ? photos.data : photos;
    const initialNextCursor = isPaginated ? photos.next_cursor : null;
    const initialHasMore = isPaginated ? photos.has_more : false;

    useEffect(() => {
        if (!has_account) {
            setOpen(true);
        }
    }, []);

    const onEffectChange = useCallback((e) => {
        const value = e.target.value;
        setEffect(value);
        const url = new URL(window.location.href);
        url.searchParams.set('effect_id', value);
        router.visit(url.toString(), {
            only: ['photos'],
            preserveScroll: true,
            preserveState: true,
        });
    }, []);

    const onParticipantChange = useCallback((e) => {
        const value = e.target.value;
        setParticipant(value);
        const url = new URL(window.location.href);
        if (value) {
            url.searchParams.set('user_id', value);
        } else {
            url.searchParams.delete('user_id');
        }
        router.visit(url.toString(), {
            only: ['photos'],
            preserveScroll: true,
            preserveState: true,
        });
    }, []);

    const currentEffectLabel = effects.find(e => String(e.value) === String(effect))?.label ?? effects[0]?.label;
    const currentParticipantLabel = participants.find(p => String(p.id) === String(participant))?.name ?? t('Everyone');

    const isViewing = event.can_view_photos || viewOwn;
    const title = (viewOwn && !event.can_view_photos) || (event.reveal_type === 'hidden' && !event.is_organizer)
        ? t('Your photos')
        : t('Gallery');
    const reveal = event.reveal_at_formatted;
    // mirrors the ternary below: the grid (and its filter bar) renders in every
    // case except the one where HiddenAlbum takes over instead
    const showingAlbum = isViewing || event.reveal_type !== 'after';

    return (
        <Layout>
            <Head title={`${event.name} - ${pageTitleSuffix}`} />
            <div className="flex flex-col px-5 h-svh">
                {(event.reveal_type === 'after' || event.reveal_type === 'during') && !has_account && !event.can_view_photos &&
                    <DarkModal open={open} onClose={() => setOpen(false)} center>
                        <AuthForm isAfter={event.reveal_type === 'after'} event={event} />
                    </DarkModal>}

                <div className="sticky top-0 z-10 pt-5 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            {allowBack && <Link href={backUrl}>
                                <div className="mr-3 button-backdrop">
                                    <ArrowLeftIcon className="w-[25px] h-[25px] text-white-foreground" />
                                </div>
                            </Link>}
                            <div>
                                <h1 className="text-white-foreground leading-tight text-xl md:text-2xl font-bold">{title}</h1>
                                <span className="text-white-foreground leading-tight text-sm">{event.name}</span>
                                {(event.media_count != null || event.starts_at_formatted) && (
                                    <div className="text-white-foreground/70 leading-tight text-xs">
                                        {event.media_count != null && t('{{count}} photos & videos', {count: event.media_count})}
                                        {event.media_count != null && event.starts_at_formatted && ' · '}
                                        {event.starts_at_formatted}
                                    </div>
                                )}
                            </div>
                        </div>
                        {uploadUrl && <a
                            href={uploadUrl}
                            className="button-backdrop flex items-center gap-1.5 text-white-foreground px-3 py-2"
                        >
                            <ArrowUpTrayIcon className="w-5 h-5" />
                            <span className="text-sm font-medium">{t('Upload')}</span>
                        </a>}
                    </div>
                </div>

                {event.reveal_type === 'hidden' && !event.is_organizer && (
                    <p className="text-white-foreground text-xs mt-2">
                        {t("The organizer of this event chose to hide other people's photos. You can only view your own photos.")}
                    </p>
                )}
                {viewOwn && !event.can_view_photos && (
                    <p className="text-white-foreground text-xs mt-1">
                        <Trans i18nKey="revealComeBack">Photos will be revealed at <span
                            className="font-semibold">{{reveal}}</span>. Please come back then.</Trans>
                    </p>
                )}

                {!isViewing && event.reveal_type === 'after' ? (
                    <HiddenAlbum reveal={reveal} onViewOwn={() => setViewOwn(true)} />
                ) : (
                    <Album reloadUrl={reloadUrl} event={event} effect={effect} participant={participant}
                           aspectRatio={aspectRatio} photos={items}
                           nextCursor={initialNextCursor} hasMore={initialHasMore} />
                )}
                {showBranding && (
                    <div className={showingAlbum ? 'pb-16' : ''}>
                        <MadeWithBadge />
                    </div>
                )}
            </div>

            {showingAlbum && (
                <div className="floating-pill-bar-wrap">
                    <div className="floating-pill-bar">
                        <div className="floating-pill">
                            <SparklesIcon className="w-3.5 h-3.5 text-white/80 shrink-0" />
                            <span className="floating-pill-label">{currentEffectLabel}</span>
                            <select
                                aria-label={t('Filter effect')}
                                name="effect"
                                className="floating-pill-select-overlay"
                                onChange={onEffectChange}
                                value={effect}
                            >
                                {effects.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                            </select>
                        </div>
                        {participants.length > 0 && (
                            <>
                                <div className="floating-pill-divider" />
                                <div className="floating-pill">
                                    <UserIcon className="w-3.5 h-3.5 text-white/80 shrink-0" />
                                    <span className="floating-pill-label">{currentParticipantLabel}</span>
                                    <select
                                        aria-label={t('Filter by guest')}
                                        name="participant"
                                        className="floating-pill-select-overlay"
                                        onChange={onParticipantChange}
                                        value={participant}
                                    >
                                        <option value="">{t('Everyone')}</option>
                                        {participants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
}
